"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STANDARD_CUSTOM_APP_DATABASE_TITLES = exports.STANDARD_CUSTOM_APP_DATABASE_COLUMN_DEFS = exports.STANDARD_CUSTOM_APP_DATABASE_RELATION_DEFS = exports.CUSTOM_APP_CONTENT_COLUMN_MANAGED_BY_PREFIX = void 0;
exports.createCustomAppContentColumnManagedBy = createCustomAppContentColumnManagedBy;
exports.createProtectedCustomAppStandardColumnData = createProtectedCustomAppStandardColumnData;
exports.buildProtectedCustomAppStandardColumnSpecs = buildProtectedCustomAppStandardColumnSpecs;
exports.standardCustomAppDatabaseRelationAppliesToSource = standardCustomAppDatabaseRelationAppliesToSource;
const database_identity_1 = require("@streamlain/database-identity");
const custom_app_config_1 = require("./custom-app-config");
const publication_contract_1 = require("./publication-contract");
exports.CUSTOM_APP_CONTENT_COLUMN_MANAGED_BY_PREFIX = 'custom-app-content';
function createCustomAppContentColumnManagedBy(contentModel) {
    return `${exports.CUSTOM_APP_CONTENT_COLUMN_MANAGED_BY_PREFIX}:${contentModel}`;
}
function createProtectedCustomAppStandardColumnData(input) {
    const contentModel = input.contentModel ?? (0, custom_app_config_1.getDefaultCustomAppContentModelForRole)(input.role);
    return {
        ...(input.data ? structuredClone(input.data) : {}),
        managedBy: createCustomAppContentColumnManagedBy(contentModel),
        systemManaged: true,
    };
}
function buildProtectedCustomAppStandardColumnSpecs(input) {
    return exports.STANDARD_CUSTOM_APP_DATABASE_COLUMN_DEFS[input.role].map(column => {
        const def = column;
        const id = input.createId();
        const publication = def.data?.publication;
        return {
            id,
            type: def.type,
            name: def.name,
            data: createProtectedCustomAppStandardColumnData({
                role: input.role,
                contentModel: input.contentModel,
                data: publication
                    ? {
                        ...def.data,
                        publication: {
                            ...publication,
                            field: id,
                            semanticKey: publication.field,
                        },
                    }
                    : def.data,
            }),
        };
    });
}
exports.STANDARD_CUSTOM_APP_DATABASE_RELATION_DEFS = [
    {
        id: 'navigation-page',
        sourceRole: 'navigation',
        sourceName: 'page',
        sourceCardinality: 'one',
        targetRole: 'pages',
        inverseName: 'navigationItems',
        inverseCardinality: 'many',
    },
    {
        id: 'topic-cluster',
        sourceRole: 'topics',
        sourceName: 'cluster',
        sourceCardinality: 'one',
        targetRole: 'clusters',
        inverseName: 'topics',
        inverseCardinality: 'many',
    },
    {
        id: 'content-topic',
        sourceRole: 'content',
        sourceName: 'topic',
        sourceCardinality: 'one',
        targetRole: 'topics',
        inverseName: 'content',
        inverseCardinality: 'many',
    },
    {
        id: 'content-author',
        sourceRole: 'content',
        sourceName: 'author',
        sourceCardinality: 'one',
        targetRole: 'users',
        inverseName: 'content',
        inverseCardinality: 'many',
    },
    {
        id: 'article-block',
        sourceRole: 'articleBlocks',
        sourceName: 'article',
        sourceCardinality: 'one',
        targetRole: 'content',
        inverseName: 'articleBlocks',
        inverseCardinality: 'many',
    },
    {
        id: 'dashboard-owner',
        sourceRole: 'content',
        sourceName: 'owner',
        sourceCardinality: 'one',
        targetRole: 'users',
        inverseName: 'dashboardItems',
        inverseCardinality: 'many',
    },
    {
        id: 'submission-form',
        sourceRole: 'submissions',
        sourceName: 'form',
        sourceCardinality: 'one',
        targetRole: 'forms',
        inverseName: 'submissions',
        inverseCardinality: 'many',
    },
    // sl-sbux.8.1 amendment 2026-05-23: StyleVariants reference their parent
    // StylePreset. Each preset can carry many variants (hover/focus/active *
    // base/mobile/desktop/reduced-motion combinations); each variant belongs
    // to exactly one preset.
    {
        id: 'variant-preset',
        sourceRole: 'styleVariants',
        sourceName: 'preset',
        sourceCardinality: 'one',
        targetRole: 'stylePresets',
        inverseName: 'variants',
        inverseCardinality: 'many',
    },
];
function standardCustomAppDatabaseRelationAppliesToSource(spec, source) {
    if (source.role !== spec.sourceRole)
        return false;
    const contentModel = source.contentModel ?? (0, custom_app_config_1.getDefaultCustomAppContentModelForRole)(source.role);
    if (spec.id === 'content-topic') {
        return contentModel === 'knowledge-base-entry';
    }
    if (spec.id === 'content-author') {
        return (contentModel === 'knowledge-base-entry' || contentModel === 'news-article');
    }
    if (spec.id === 'dashboard-owner') {
        return contentModel === 'intranet-dashboard-item';
    }
    return true;
}
const STATUS_OPTIONS = publication_contract_1.PUBLICATION_STATUS_OPTIONS;
const SUBMISSION_STATUS_OPTIONS = [
    { id: 'new', value: 'new', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    {
        id: 'processed',
        value: 'processed',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
    },
    { id: 'failed', value: 'failed', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red },
];
const DASHBOARD_ITEM_TYPE_OPTIONS = [
    { id: 'kpi', value: 'KPI', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    { id: 'task', value: 'Task', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green },
    {
        id: 'incident',
        value: 'Incident',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red,
    },
    {
        id: 'project',
        value: 'Project',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
    },
    {
        id: 'request',
        value: 'Request',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow,
    },
];
const DASHBOARD_WORK_STATUS_OPTIONS = [
    { id: 'todo', value: 'Todo', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey },
    { id: 'doing', value: 'Doing', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    {
        id: 'blocked',
        value: 'Blocked',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red,
    },
    { id: 'done', value: 'Done', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green },
];
const DASHBOARD_PRIORITY_OPTIONS = [
    { id: 'low', value: 'Low', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey },
    { id: 'medium', value: 'Medium', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    { id: 'high', value: 'High', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow },
    {
        id: 'critical',
        value: 'Critical',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red,
    },
];
exports.STANDARD_CUSTOM_APP_DATABASE_COLUMN_DEFS = {
    branding: [
        { name: 'brandingId', type: 'rich-text' },
        { name: 'displayName', type: 'rich-text' },
        { name: 'logoSource', type: 'rich-text' },
        { name: 'logoBlobId', type: 'rich-text' },
        { name: 'faviconSource', type: 'rich-text' },
        { name: 'faviconBlobId', type: 'rich-text' },
        { name: 'socialImageSource', type: 'rich-text' },
        { name: 'socialImageBlobId', type: 'rich-text' },
        { name: 'updatedAt', type: 'rich-text' },
    ],
    pages: [
        { name: 'pageId', type: 'rich-text' },
        { name: 'path', type: 'rich-text' },
        { name: 'routeKind', type: 'rich-text' },
        { name: 'audience', type: 'rich-text' },
        { name: 'templateKey', type: 'rich-text' },
        { name: 'collectionRole', type: 'rich-text' },
        { name: 'publicationModel', type: 'rich-text' },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        { name: 'title', type: 'rich-text' },
        { name: 'dataSourceKeys', type: 'rich-text' },
        { name: 'navigationKey', type: 'rich-text' },
        { name: 'parentPageId', type: 'rich-text' },
        { name: 'order', type: 'number' },
    ],
    navigation: [
        { name: 'navId', type: 'rich-text' },
        { name: 'label', type: 'rich-text' },
        { name: 'pageId', type: 'rich-text' },
        { name: 'href', type: 'rich-text' },
        { name: 'parentId', type: 'rich-text' },
        { name: 'order', type: 'number' },
    ],
    clusters: [
        { name: 'clusterId', type: 'rich-text' },
        {
            name: 'slug',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.SLUG, required: true },
            },
        },
        {
            name: 'title',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.TITLE, required: true },
            },
        },
        {
            name: 'description',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
                    required: true,
                },
            },
        },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        {
            name: 'canonical',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CANONICAL } },
        },
        {
            name: 'locale',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.LOCALE } },
        },
        { name: 'order', type: 'number' },
    ],
    topics: [
        { name: 'topicId', type: 'rich-text' },
        {
            name: 'clusterId',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
                    relation: {
                        targetSourceKey: 'clusters',
                        targetColumnKey: publication_contract_1.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
                    },
                    required: true,
                },
            },
        },
        {
            name: 'slug',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.SLUG, required: true },
            },
        },
        {
            name: 'title',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.TITLE, required: true },
            },
        },
        {
            name: 'description',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
                    required: true,
                },
            },
        },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        {
            name: 'canonical',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CANONICAL } },
        },
        {
            name: 'locale',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.LOCALE } },
        },
        { name: 'order', type: 'number' },
    ],
    users: [
        { name: 'userId', type: 'rich-text' },
        { name: 'workspaceUserId', type: 'rich-text' },
        { name: 'tenantUserId', type: 'rich-text' },
        { name: 'email', type: 'rich-text' },
        { name: 'role', type: 'rich-text' },
        {
            name: 'slug',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.SLUG, required: true },
            },
        },
        {
            name: 'title',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.TITLE, required: true },
            },
        },
        {
            name: 'description',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.DESCRIPTION },
            },
        },
        { name: 'avatar', type: 'rich-text' },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        {
            name: 'canonical',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CANONICAL } },
        },
        {
            name: 'locale',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.LOCALE } },
        },
        { name: 'order', type: 'number' },
    ],
    content: [
        {
            name: 'slug',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.SLUG, required: true },
            },
        },
        {
            name: 'title',
            type: 'rich-text',
            data: {
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.TITLE, required: true },
            },
        },
        {
            name: 'description',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
                    required: true,
                },
            },
        },
        { name: 'summary', type: 'rich-text' },
        { name: 'body', type: 'rich-text' },
        {
            name: 'blocks',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.BODY } },
        },
        {
            name: 'image',
            type: 'attachment',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.IMAGE } },
        },
        { name: 'imageAlt', type: 'rich-text' },
        { name: 'ctaLabel', type: 'rich-text' },
        { name: 'ctaUrl', type: 'rich-text' },
        { name: 'featured', type: 'checkbox' },
        { name: 'readTime', type: 'rich-text' },
        {
            name: 'itemType',
            type: 'select',
            data: { options: DASHBOARD_ITEM_TYPE_OPTIONS },
        },
        {
            name: 'workStatus',
            type: 'select',
            data: { options: DASHBOARD_WORK_STATUS_OPTIONS },
        },
        {
            name: 'priority',
            type: 'select',
            data: { options: DASHBOARD_PRIORITY_OPTIONS },
        },
        { name: 'value', type: 'number' },
        { name: 'target', type: 'number' },
        { name: 'unit', type: 'rich-text' },
        { name: 'trend', type: 'number' },
        { name: 'category', type: 'rich-text' },
        { name: 'deadline', type: 'date' },
        { name: 'updatedAt', type: 'rich-text' },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        {
            name: 'publishedAt',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.PUBLISHED_AT } },
        },
        {
            name: 'scheduledAt',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.SCHEDULED_AT } },
        },
        {
            name: 'canonical',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CANONICAL } },
        },
        {
            name: 'hreflangGroup',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.HREFLANG_GROUP } },
        },
        {
            name: 'locale',
            type: 'rich-text',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.LOCALE } },
        },
        {
            name: 'requiredLocale',
            type: 'checkbox',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.REQUIRED_LOCALE } },
        },
        {
            name: 'noindex',
            type: 'checkbox',
            data: { publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.NOINDEX } },
        },
        {
            name: 'clusterId',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
                    relation: {
                        targetSourceKey: 'clusters',
                        targetColumnKey: publication_contract_1.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
                    },
                },
            },
        },
        {
            name: 'topicId',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.TOPIC_ID,
                    relation: {
                        targetSourceKey: 'topics',
                        targetColumnKey: publication_contract_1.PUBLICATION_COLUMN_KEYS.TOPIC_ID,
                    },
                },
            },
        },
        {
            name: 'internalLinkRowIds',
            type: 'rich-text',
            data: {
                publication: {
                    field: publication_contract_1.PUBLICATION_COLUMN_KEYS.INTERNAL_LINK_ROW_IDS,
                    relation: {
                        targetSourceKey: 'content',
                        targetColumnKey: publication_contract_1.PUBLICATION_COLUMN_KEYS.SLUG,
                    },
                },
            },
        },
    ],
    articleBlocks: [
        { name: 'blockId', type: 'rich-text' },
        { name: 'articleId', type: 'rich-text' },
        { name: 'order', type: 'number' },
        {
            name: 'type',
            type: 'select',
            data: {
                options: [
                    {
                        id: 'hero',
                        value: 'hero',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue,
                    },
                    {
                        id: 'prose',
                        value: 'prose',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
                    },
                    {
                        id: 'image',
                        value: 'image',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow,
                    },
                    {
                        id: 'imageText',
                        value: 'imageText',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
                    },
                    {
                        id: 'callout',
                        value: 'callout',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue,
                    },
                    {
                        id: 'quote',
                        value: 'quote',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey,
                    },
                    { id: 'code', value: 'code', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red },
                    {
                        id: 'steps',
                        value: 'steps',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
                    },
                    {
                        id: 'table',
                        value: 'table',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow,
                    },
                    { id: 'cta', value: 'cta', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
                    {
                        id: 'relatedArticles',
                        value: 'relatedArticles',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
                    },
                    {
                        id: 'embed_video',
                        value: 'embed_video',
                        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Red,
                    },
                ],
            },
        },
        { name: 'enabled', type: 'checkbox' },
        { name: 'heading', type: 'rich-text' },
        { name: 'headingLevel', type: 'rich-text' },
        { name: 'bodyMarkdown', type: 'rich-text' },
        { name: 'image', type: 'attachment' },
        { name: 'imageAlt', type: 'rich-text' },
        { name: 'caption', type: 'rich-text' },
        { name: 'focalX', type: 'number' },
        { name: 'focalY', type: 'number' },
        { name: 'cropMode', type: 'rich-text' },
        { name: 'displayWidth', type: 'rich-text' },
        { name: 'propsJson', type: 'rich-text' },
        {
            name: 'status',
            type: 'select',
            data: {
                options: STATUS_OPTIONS,
                publication: { field: publication_contract_1.PUBLICATION_COLUMN_KEYS.STATUS, required: true },
            },
        },
        { name: 'updatedAt', type: 'rich-text' },
    ],
    translations: [
        { name: 'entityDb', type: 'rich-text' },
        { name: 'entityRowId', type: 'rich-text' },
        { name: 'field', type: 'rich-text' },
        { name: 'locale', type: 'rich-text' },
        { name: 'value', type: 'rich-text' },
        { name: 'status', type: 'select', data: { options: STATUS_OPTIONS } },
        { name: 'sourceHash', type: 'rich-text' },
        { name: 'updatedAt', type: 'rich-text' },
    ],
    forms: [
        { name: 'formKey', type: 'rich-text' },
        { name: 'title', type: 'rich-text' },
        { name: 'formConfig', type: 'rich-text' },
        { name: 'submitLabel', type: 'rich-text' },
        { name: 'successMessage', type: 'rich-text' },
        { name: 'status', type: 'select', data: { options: STATUS_OPTIONS } },
    ],
    submissions: [
        { name: 'formKey', type: 'rich-text' },
        { name: 'payload', type: 'rich-text' },
        {
            name: 'status',
            type: 'select',
            data: { options: SUBMISSION_STATUS_OPTIONS },
        },
        { name: 'submittedAt', type: 'rich-text' },
        { name: 'workflowRunId', type: 'rich-text' },
    ],
    // sl-sbux.8.1: database-native styling DBs. Themes drives app-wide tokens
    // + custom CSS, MotionSpecs/StylePresets/CustomSheets/CustomHtmlBlocks
    // replace the legacy `ViewConfig.payload.{motion,styles,theme.customCss}`
    // blob fields. Only `themes.active` is mutated outside of the wizard's
    // own write path — exactly one row is active at any time.
    themes: [
        { name: 'name', type: 'rich-text' },
        { name: 'tokens', type: 'rich-text' },
        { name: 'customCss', type: 'rich-text' },
        { name: 'aiThreadId', type: 'rich-text' },
        { name: 'active', type: 'checkbox' },
        { name: 'createdAt', type: 'rich-text' },
        { name: 'updatedAt', type: 'rich-text' },
    ],
    motionSpecs: [
        { name: 'specId', type: 'rich-text' },
        { name: 'preset', type: 'rich-text' },
        { name: 'trigger', type: 'rich-text' },
        { name: 'easing', type: 'rich-text' },
        { name: 'durationMs', type: 'rich-text' },
        { name: 'delayMs', type: 'rich-text' },
        { name: 'staggerMs', type: 'rich-text' },
        { name: 'reducedMotionPolicy', type: 'rich-text' },
        { name: 'componentScope', type: 'rich-text' },
    ],
    stylePresets: [
        { name: 'presetId', type: 'rich-text' },
        { name: 'target', type: 'rich-text' },
        { name: 'surface', type: 'rich-text' },
        { name: 'contentRhythm', type: 'rich-text' },
        { name: 'tokens', type: 'rich-text' },
    ],
    // sl-sbux.8.1 amendment 2026-05-23: StyleVariants own row-per-variant
    // CRUD. `state`/`media` columns scope the override; `tokens` holds the
    // partial `Record<StyleTokenName, StyleTokenValue>` JSON-encoded; the
    // `presetRef` relation column is provisioned by
    // `STANDARD_CUSTOM_APP_DATABASE_RELATION_DEFS.variant-preset`.
    styleVariants: [
        { name: 'variantId', type: 'rich-text' },
        { name: 'state', type: 'rich-text' },
        { name: 'media', type: 'rich-text' },
        { name: 'tokens', type: 'rich-text' },
    ],
    customSheets: [
        { name: 'name', type: 'rich-text' },
        { name: 'content', type: 'rich-text' },
        { name: 'enabled', type: 'checkbox' },
        { name: 'order', type: 'rich-text' },
        { name: 'media', type: 'rich-text' },
        { name: 'externalUrl', type: 'rich-text' },
    ],
    customHtmlBlocks: [
        { name: 'name', type: 'rich-text' },
        { name: 'template', type: 'rich-text' },
        { name: 'dataSourceRef', type: 'rich-text' },
        { name: 'usedIn', type: 'rich-text' },
    ],
};
exports.STANDARD_CUSTOM_APP_DATABASE_TITLES = {
    branding: 'Branding',
    pages: 'Pages',
    navigation: 'Navigation',
    clusters: 'Clusters',
    topics: 'Topics',
    users: 'Users',
    content: 'Content',
    articleBlocks: 'Article Blocks',
    translations: 'Translations',
    forms: 'Forms',
    submissions: 'Submissions',
    themes: 'Themes',
    motionSpecs: 'Motion Specs',
    stylePresets: 'Style Presets',
    styleVariants: 'Style Variants',
    customSheets: 'Custom Sheets',
    customHtmlBlocks: 'Custom HTML Blocks',
};
